import {
	ChangeDetectionStrategy,
	Component,
	Input,
	OnInit,
	Output,
	EventEmitter,
	ViewChild,
} from '@angular/core';
import { BaseComponent } from '@core/base.component';
import { ICON } from '@core/util/icon';
import { DATE_FORMAT } from '@models/businesses';
import {
	DepositHolder,
	DepositRecord,
	MatterAdjustmentDeposit,
} from '@models/matter';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { Select } from '@ngxs/store';
import { AdjustmentsSelector } from '@state/matter/adjustments/adjustments.selectors';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { BehaviorSubject, Observable, combineLatest } from 'rxjs';
import { format } from 'date-fns';
import { take } from 'rxjs/operators';
import { AdjustmentsDispatch } from '@state/matter/adjustments';
import {
	DepositHolderDropdownComponent,
	DepositHolderDropdownMode,
} from '../../deposit-holder-dropdown/deposit-holder-dropdown.component';
import { MatterDispatch } from '@state/matter';
import { TestHooks } from './deposit-drawer.hooks';
import { createId } from 'src/app/shared/core/util/file';

export const DefaultDepositHolderValue = DepositHolder.VENDOR_BROKERAGE;

type DepositDrawerVM = {
	deposits: DepositRecord[];
	depositHolder: DepositHolder;
};

const DefaultDepositDrawerVM: DepositDrawerVM = {
	deposits: [],
	depositHolder: DefaultDepositHolderValue,
};

@UntilDestroy()
@Component({
	selector: 'app-deposit-drawer',
	templateUrl: './deposit-drawer.component.html',
	styleUrls: ['./deposit-drawer.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class DepositDrawerComponent
	extends BaseComponent<DepositDrawerVM>
	implements OnInit
{
	testHooks = TestHooks;
	public ICON = ICON;
	public depositHolderDropdownMode = DepositHolderDropdownMode;

	public opened$ = new BehaviorSubject(false);
	@Input() set opened(openedState: boolean) {
		this.opened$.next(openedState);
	}

	@Output() depositHolderSelectionChange: EventEmitter<DepositHolder> =
		new EventEmitter();

	@ViewChild('depositHolderDropdown')
	depositHolderDropdown: DepositHolderDropdownComponent;

	@SelectSnapshot(SelectedMatter.id) matterId: string;
	@Select(AdjustmentsSelector.depositAdjustment)
	depositAdjustment$: Observable<MatterAdjustmentDeposit>;
	@SelectSnapshot(SelectedMatter.agreementDate) agreementDate: string;
	@Select(SelectedMatter.getSelectedDepositHolder)
	depositHolder$: Observable<DepositHolder>;

	constructor(
		private adjustmentsDispatch: AdjustmentsDispatch,
		private matterDispatch: MatterDispatch,
	) {
		super(structuredClone(DefaultDepositDrawerVM));
	}

	ngOnInit() {
		this.depositAdjustment$
			.pipe(untilDestroyed(this))
			.subscribe((depositAdjustment) => {
				const deposits = structuredClone(depositAdjustment.deposits);
				this.VM = { deposits };
			});
	}

	toggleDrawerOpenState() {
		const currOpenedState = this.opened$.getValue();

		if (currOpenedState) {
			this.closeDrawer();
		} else {
			this.openDrawer();
		}
	}

	openDrawer() {
		this.opened$.next(true);
	}

	closeDrawer() {
		this.opened$.next(false);
	}

	discardChangesAndCloseDrawer() {
		combineLatest([this.depositAdjustment$, this.depositHolder$])
			.pipe(take(1))
			.subscribe(([depositAdjustment, depositHolder]) => {
				const deposits = structuredClone(depositAdjustment.deposits);
				this.VM = { deposits, depositHolder };

				// discard any changes made to the dropdown by resetting it
				this.depositHolderDropdown.resetDropdownSelection();

				this.closeDrawer();
			});
	}

	saveChangesAndCloseDrawer() {
		this.matterDispatch.SetDepositHolder(this.matterId, this.VM.depositHolder);

		this.depositAdjustment$.pipe(take(1)).subscribe((depositAdjustment) => {
			this.adjustmentsDispatch.Set({
				...depositAdjustment,
				deposits: this.VM.deposits,
			} as MatterAdjustmentDeposit);

			this.closeDrawer();
		});
	}

	addDeposit() {
		const deposits = structuredClone(this.VM.deposits);
		deposits.push({
			id: createId(),
			amount: 0,
			date: this.agreementDate ?? format(new Date(), DATE_FORMAT),
		});
		this.VM = { deposits };
	}

	removeDeposit(index: number) {
		const deposits = structuredClone(this.VM.deposits).filter(
			(x, i) => i !== index,
		);
		this.VM = { deposits };
	}

	updateDepositAmount(amount: number, depositIndex: number) {
		const deposits = structuredClone(this.VM.deposits);
		deposits[depositIndex].amount = amount;
		this.VM = { deposits };
	}

	updateDepositDate(date: string, depositIndex: number) {
		const deposits = structuredClone(this.VM.deposits);
		deposits[depositIndex].date = date;
		this.VM = { deposits };
	}

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	depositTrackByFn(index: number, depositItem: DepositRecord) {
		return index;
	}

	onDepositHolderSelect(depositHolder: DepositHolder) {
		this.VM = { depositHolder };

		this.depositHolderSelectionChange.emit(depositHolder);
	}
}
